<template>
  <div class="flex flex-col gap-1.5">
    <label v-if="props.label || slots.label" :id="labelId" :for="id" class="font-bold text-white">
      <slot name="label">{{ props.label }}</slot>
    </label>
    <button
      :id="id"
      ref="trigger-ref"
      type="button"
      class="flex min-h-9.5 w-full items-center gap-2 rounded-md border bg-surface px-3 py-2 text-left leading-5 outline-none"
      :class="[BORDER_CLASSES[validationResult?.[0] ?? 'default'], props.readonly ? 'cursor-default text-white/60' : 'cursor-pointer text-white/90']"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-disabled="props.readonly || undefined"
      :aria-labelledby="props.label ? `${labelId} ${valueId}` : valueId"
      :aria-describedby="descriptionId"
      @click="toggle"
    >
      <span v-if="slots.value && selectedOption" :id="valueId" class="flex min-w-0 flex-auto">
        <slot name="value" :option="selectedOption" />
      </span>
      <span v-else-if="slots.option && selectedOption" :id="valueId" class="flex min-w-0 flex-auto items-center gap-2 truncate">
        <slot name="option" :option="selectedOption" />
      </span>
      <span v-else-if="!selectedOption && props.placeholder" :id="valueId" class="min-w-0 flex-auto truncate text-white/40">{{ props.placeholder }}</span>
      <span v-else :id="valueId" class="flex min-w-0 flex-auto items-center gap-2">
        <template v-if="selectedOption?.image">
          <SFIcon v-if="isIconName(selectedOption.image)" :name="selectedOption.image" class="text-white/60" :class="{ 'order-last': selectedOption.imagePosition === 'right' }" />
          <img v-else :src="selectedOption.image" alt="" class="size-5 object-contain" :class="{ 'order-last': selectedOption.imagePosition === 'right' }" />
        </template>
        <span class="flex min-w-0 flex-auto flex-col">
          <span class="truncate" :class="{ 'text-accent': selectedOption?.accent }" :style="{ color: selectedOption?.color }">{{ selectedOption?.label }}</span>
          <span v-if="selectedOption?.description" class="truncate text-xs text-white/50">{{ selectedOption.description }}</span>
        </span>
      </span>
      <SFIcon v-if="!props.readonly" name="chevron-down" class="text-white/60" :class="{ 'rotate-180': open }" />
    </button>
    <SFValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" />
    <SFParagraph v-if="props.description" :id="descriptionId" type="muted" size="sm">{{ props.description }}</SFParagraph>
    <Teleport to="body">
      <SFDropdownMenu v-if="open && position" :anchor="position" :width="position.right - position.left" float="right" position="bottom" :class="{ 'scroll-pt-12': props.search }" @close="close" @scroll="loadMore">
        <div v-if="props.search" class="sticky -top-1 z-10 -mx-1 -mt-1 bg-surface p-1">
          <input v-model="query" type="search" :aria-label="props.label" class="w-full rounded border border-line bg-page px-3 py-2 leading-5 text-white/90 outline-none focus:border-accent" @input="showPage(0)" @keydown.enter.prevent="selectFirstMatch" />
        </div>
        <ul ref="list-ref" role="menu" class="flex flex-col">
          <template v-for="(option, index) in renderedOptions" :key="renderStart + index">
            <li v-if="!isSelectable(option)" :role="option.type === 'divider' ? 'separator' : 'presentation'" :class="option.type === 'divider' ? 'mx-1 my-1 border-t border-line' : 'px-3 pt-2 pb-1 text-xs font-bold text-white/50 uppercase'">
              {{ option.type === 'header' ? option.label : '' }}
            </li>
            <li v-else role="none">
              <button
                type="button"
                role="menuitem"
                :data-selected="option.value === modelValue || undefined"
                :disabled="option.disabled"
                class="flex w-full items-center gap-3 rounded px-3 py-2 text-left outline-none enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                :class="[option.value === modelValue ? 'bg-accent/15' : 'enabled:hover:bg-surface-hover focus-visible:bg-surface-hover', { 'text-accent': option.accent }]"
                @click="select(option.value)"
              >
                <slot name="option" :option="option">
                  <template v-if="option.image">
                    <SFIcon v-if="isIconName(option.image)" :name="option.image" class="text-white/60" :class="{ 'order-last': option.imagePosition === 'right' }" />
                    <img v-else :src="option.image" alt="" class="size-5 object-contain" :class="{ 'order-last': option.imagePosition === 'right' }" />
                  </template>
                  <span class="flex min-w-0 flex-1 flex-col">
                    <span :style="{ color: option.color }">{{ option.label }}</span>
                    <span v-if="option.description" class="text-xs text-white/50">{{ option.description }}</span>
                  </span>
                </slot>
              </button>
            </li>
          </template>
        </ul>
      </SFDropdownMenu>
    </Teleport>
  </div>
</template>

<script setup lang="ts" generic="TValue">
import { computed, ref, useId, useTemplateRef } from 'vue'
import { isSelectable, usePagedList, type SelectDivider, type SelectHeader, type SelectOption } from '@utils/components'
import { isIconName } from '@utils/icons'
import { useDropdown } from '@utils/interactions'
import { createDefaultValidator, useValidation, type ValidationProps } from '@utils/validations'
import SFDropdownMenu from './SFDropdownMenu.vue'
import SFIcon from './SFIcon.vue'
import SFParagraph from './SFParagraph.vue'
import SFValidation from './SFValidation.vue'

defineOptions({
  name: 'SFSelect'
})

const props = defineProps<
  ValidationProps<TValue> & {
    /**
     * Text shown above the field, also its accessible name
     */
    label?: string
    /**
     * Smaller grey text shown under the field
     */
    description?: string
    /**
     * Options to pick from, with optional header rows and dividers between them
     */
    options: SelectOption<TValue>[]
    /**
     * Grey text shown in the field while no option is picked
     */
    placeholder?: string
    /**
     * Shows an error while no option is picked
     */
    required?: boolean
    /**
     * Shows a search field above the options that filters them by label and description
     */
    search?: boolean
    /**
     * Renders this many options at a time and adds more while the list is scrolled
     */
    pageSize?: number
    /**
     * Shows the value without letting it be changed
     */
    readonly?: boolean
  }
>()

const modelValue = defineModel<TValue>({ required: true })

const slots = defineSlots<{
  /**
   * Replaces the label text
   */
  label?(): unknown
  /**
   * Replaces the image and label of an option, in the list and in the field
   */
  option?(props: { option: Exclude<SelectOption<TValue>, SelectHeader | SelectDivider> }): unknown
  /**
   * Replaces the content of the field for the picked option, the list keeps using the option slot
   */
  value?(props: { option: Exclude<SelectOption<TValue>, SelectHeader | SelectDivider> }): unknown
}>()

defineExpose({
  get isValid() {
    return isValid.value
  }
})

const BORDER_CLASSES = {
  default: 'border-line focus-visible:border-accent',
  error: 'border-red-400',
  warning: 'border-yellow-400'
}

const id = useId()
const labelId = useId()
const valueId = useId()

const descriptionId = computed(() => (props.description ? `${id}-description` : undefined))

const query = ref('')

const triggerElement = useTemplateRef('trigger-ref')
const listElement = useTemplateRef('list-ref')

const { validationVisible, validationResult, isValid } = useValidation(modelValue, props, createDefaultValidator(props))

const selectableOptions = computed(() => props.options.filter(isSelectable))

const selectedOption = computed(() => selectableOptions.value.find((option) => option.value === modelValue.value))

const matchingOptions = computed((): SelectOption<TValue>[] => {
  const search = query.value.trim().toLowerCase()

  return search ? selectableOptions.value.filter((option) => option.label.toLowerCase().includes(search) || option.description?.toLowerCase().includes(search)) : props.options
})

const { renderStart, renderedItems: renderedOptions, showPage, loadMore } = usePagedList(matchingOptions, () => props.pageSize, listElement)

const { open, position, toggle: toggleOpen, close } = useDropdown(triggerElement, triggerElement)

function toggle() {
  if (props.readonly || selectableOptions.value.length === 0) return

  query.value = ''
  toggleOpen()

  if (open.value) {
    showPage(matchingOptions.value.findIndex((option) => isSelectable(option) && option.value === modelValue.value))
  }
}

function select(value: TValue) {
  modelValue.value = value

  validationVisible.value = true

  close()
}

function selectFirstMatch() {
  const option = matchingOptions.value.filter(isSelectable).find((entry) => !entry.disabled)

  if (option) {
    select(option.value)
  }
}
</script>
